// card-carousel-banner.component.ts
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, QueryList, Renderer2, ViewChildren } from '@angular/core';
import { BasicCardData, CardCarouselBanner, TitleInfo } from '../../../common/interfaces';
import { NGXLogger } from 'ngx-logger';
import { isPlatformServer } from '@angular/common';
import { getRoundedClass } from '../../../common/utils/styles-helper';
import { RoundedCornerSize } from '../../../common/enums';

@Component({
  selector: 'vsf-card-carousel-banner',
  templateUrl: './card-carousel-banner.component.html',
  styleUrls: ['./card-carousel-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardCarouselBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() banner!: CardCarouselBanner;
  @Input() isMobile: boolean;
  @Input() linePadding: string;
  @Input() titleInfo: TitleInfo | null = null;

  @ViewChildren('videoBox') videoBoxes!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  displayedItems: BasicCardData[];
  itemsPerSlide: number;
  itemsCount: number;
  startIndex = 0;
  sizes: Array<{ w: number; h: number }> = [];
  mediaRoundedCorners: RoundedCornerSize = RoundedCornerSize.None;
  private ros: ResizeObserver[] = [];

  ngOnInit(): void {
    this.itemsCount = this.banner?.basicCards.length;
    this.itemsPerSlide = Math.min(this.banner?.slideNumber, this.itemsCount);
    this.displayedItems = this.banner?.basicCards.slice(this.startIndex, this.startIndex + this.itemsPerSlide);
    this.mediaRoundedCorners = (this.banner?.mediaRoundedCorners || 'none') as any;
    this.setItemsPerSlide();
  }

  ngAfterViewInit(): void {
    this.bindObservers();
    this.videoBoxes.changes.subscribe(() => this.bindObservers());
  }

  ngOnDestroy(): void {
    this.unbindObservers();
  }

  get cards() { return this.banner?.basicCards ?? []; }

  getShapeClasses() {
    switch (this.banner?.basicCardShape) {
      case 'circle':
        return 'aspect-square rounded-full';
      case 'rectangle-16-9':
        return 'aspect-[16/9]';
      case 'rectangle-9-16':
        return 'aspect-[9/16]';
      case 'rectangle-7-5':
        return 'aspect-[7/5]';
      case 'rectangle-5-7':
        return 'aspect-[5/7]';
      case 'square':
      default:
        return 'aspect-square';
    }
  }

  get roundedClass(): string {
    return getRoundedClass(this.mediaRoundedCorners);
  }

  showPreviousItems(): void {
    this.startIndex = Math.max(this.startIndex - this.itemsPerSlide, 0);
    this.updateDisplayedProducts();
  }

  showNextItems(): void {
      this.startIndex = Math.min(this.startIndex + this.itemsPerSlide, this.itemsCount - this.itemsPerSlide);
      this.updateDisplayedProducts();
  }

  private updateDisplayedProducts(): void {
    if (this.startIndex + this.itemsPerSlide >= this.itemsCount) {
      this.startIndex = Math.max(this.itemsCount - this.itemsPerSlide, 0);
    }
    this.displayedItems = this.banner.basicCards.slice(this.startIndex, this.startIndex + this.itemsPerSlide);
    this.cdr.detectChanges();
    this.setItemsPerSlide();
    setTimeout(() => this.bindObservers(), 0);
  }

  private setItemsPerSlide(): void {
    const slideWidth = `${(100 / this.itemsPerSlide).toString()}%`;
    // Check if elementRef is available and slides exist
    if (this.elementRef && this.elementRef.nativeElement) {
      const slides = this.elementRef.nativeElement.querySelectorAll('.card');
      Array.from(slides).forEach((slide:any) => {
        this.renderer.setStyle(slide, 'flex', `0 0 ${slideWidth}`);
        this.renderer.setStyle(slide, 'max-width', `${slideWidth}`);
      });
    }
  }

  private bindObservers(): void {
    if(isPlatformServer(this.platformId)) {
      return;
    }
    this.unbindObservers();
    this.sizes = new Array(this.displayedItems?.length || 0).fill({ w: 0, h: 0 });

    this.videoBoxes.forEach((elRef, i) => {
      const el = elRef.nativeElement;
      const update = () => {
        const r = el.getBoundingClientRect();
        this.sizes[i] = { w: Math.round(r.width), h: Math.round(r.height) };
        this.cdr.detectChanges();
      };
      setTimeout(update, 0);
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      this.ros.push(ro);
    });
  }

  private unbindObservers(): void {
    this.ros.forEach(ro => ro.disconnect());
    this.ros = [];
  }



  trackByIdx(_i: number, _item: unknown) { return _i; }
}
